import { create } from 'zustand';
import type { SelectedItemInfo, OrderItem, AddressInfo, PriceBreakdown } from '@/types';
import type { CarryHelpClass } from '@/lib/carry-help';

interface OrderStore {
  // Step 1: 물품
  itemInfo: SelectedItemInfo;
  items: OrderItem[];

  // Step 2: 주소
  origin: AddressInfo | null;
  destination: AddressInfo | null;

  // Step 3: 운송비
  priceBreakdown: PriceBreakdown | null;
  distanceKm: number | null;

  // Step 4: 일정
  scheduleDate: string | null;
  designatedPickupSlot: string | null;
  designatedDeliverySlot: string | null;
  flexiblePickupMorning: boolean;
  flexiblePickupAfternoon: boolean;
  flexibleDeliveryMorning: boolean;
  flexibleDeliveryAfternoon: boolean;
  flexibleDeliveryAnytime: boolean;
  showDeliveryTimePanel: boolean;
  pickupDiscountRate: number;  // 0, 5, 7, 10
  deliveryDiscountRate: number;
  // 슬롯(수거/배송)을 마지막으로 선택한 시각(epoch ms) — 동시주문 충돌 검사 기준
  slotSelectedAt: number | null;
  // 동시주문 충돌로 재선택이 필요할 때 일정 스텝에 표시할 안내 (없으면 null)
  slotConflictNotice: string | null;

  // Step 5: 결제
  phone: string;
  originPhone: string;
  additionalNote: string;
  // 운반도움 (결제 스텝에서 선택: 출발지/도착지 도움 — 필수 모드면 필수, 할인 모드면 side당 3% 할인)
  carryHelpOrigin: boolean;
  carryHelpDest: boolean;
  termsAgreed: {
    terms: boolean;
    privacy: boolean;
    marketing: boolean;
  };

  // 쿠폰
  appliedCouponCode: string | null;
  couponDiscount: number;

  // 견적
  quoteId: string | null;
  // 확정 견적의 고정 최종가 (있으면 결제는 이 값 기준 — 표시·청구 고정). 없으면 null
  lockedFinalPrice: number | null;
  // 견적 발송 시 관리자가 직접 추가한 항목 (양수=가산, 음수=할인). 없으면 null
  quoteExtraItem: { name: string; amount: number } | null;
  // 견적 발송 시 관리자가 지정/자동산출한 운반도움 분류. null = 직접 주문 (items 기준 산출)
  quoteCarryHelp: CarryHelpClass | null;

  // UI
  currentStep: number;
  totalSteps: number;

  // 액션
  setItemInfo: (data: Partial<SelectedItemInfo>) => void;
  setOrigin: (data: AddressInfo | null) => void;
  setDestination: (data: AddressInfo | null) => void;
  setPriceBreakdown: (data: PriceBreakdown | null) => void;
  setDistanceKm: (km: number | null) => void;
  setSchedule: (data: {
    date?: string;
    pickupSlot?: string | null;
    deliverySlot?: string | null;
    flexPickupMorning?: boolean;
    flexPickupAfternoon?: boolean;
    flexDeliveryMorning?: boolean;
    flexDeliveryAfternoon?: boolean;
    flexDeliveryAnytime?: boolean;
    showDeliveryPanel?: boolean;
    pickupDiscount?: number;
    deliveryDiscount?: number;
  }) => void;
  setSlotSelectedAt: (ts: number | null) => void;
  setSlotConflictNotice: (msg: string | null) => void;
  setPhone: (phone: string) => void;
  setOriginPhone: (phone: string) => void;
  setAdditionalNote: (note: string) => void;
  setCarryHelp: (side: 'origin' | 'dest', value: boolean) => void;
  setQuoteCarryHelp: (mode: CarryHelpClass | null) => void;
  setTermsAgreed: (key: 'terms' | 'privacy' | 'marketing', value: boolean) => void;
  setAllTermsAgreed: (value: boolean) => void;
  addItem: () => void;
  removeItem: (uid: string) => void;
  setCoupon: (code: string | null, discount: number) => void;
  setQuoteId: (quoteId: string | null) => void;
  setLockedFinalPrice: (v: number | null) => void;
  setQuoteExtraItem: (item: { name: string; amount: number } | null) => void;
  setCurrentStep: (step: number, total?: number) => void;
  reset: () => void;
}

const initialItemInfo: SelectedItemInfo = {
  itemId: null,
  category: null,
  options: {},
  basePrice: 0,
  isCustom: false,
};

const initialTermsAgreed = {
  terms: false,
  privacy: false,
  marketing: false,
};

export const useOrderStore = create<OrderStore>((set) => ({
  // 초기 상태
  itemInfo: initialItemInfo,
  items: [],
  origin: null,
  destination: null,
  priceBreakdown: null,
  distanceKm: null,
  scheduleDate: null,
  designatedPickupSlot: null,
  designatedDeliverySlot: null,
  flexiblePickupMorning: false,
  flexiblePickupAfternoon: false,
  flexibleDeliveryMorning: false,
  flexibleDeliveryAfternoon: false,
  flexibleDeliveryAnytime: false,
  showDeliveryTimePanel: false,
  pickupDiscountRate: 0,
  deliveryDiscountRate: 0,
  slotSelectedAt: null,
  slotConflictNotice: null,
  phone: '',
  originPhone: '',
  additionalNote: '',
  carryHelpOrigin: false,
  carryHelpDest: false,
  termsAgreed: initialTermsAgreed,
  appliedCouponCode: null,
  couponDiscount: 0,
  quoteId: null,
  lockedFinalPrice: null,
  quoteExtraItem: null,
  quoteCarryHelp: null,
  currentStep: 1,
  totalSteps: 5,

  // 액션 구현
  setItemInfo: (data) =>
    set((state) => ({
      itemInfo: { ...state.itemInfo, ...data },
    })),

  setOrigin: (data) => set({ origin: data }),

  setDestination: (data) => set({ destination: data }),

  setPriceBreakdown: (data) => set({ priceBreakdown: data }),

  setDistanceKm: (km) => set({ distanceKm: km }),

  setSchedule: (data) =>
    set((state) => ({
      scheduleDate: data.date !== undefined ? data.date : state.scheduleDate,
      designatedPickupSlot: data.pickupSlot !== undefined ? data.pickupSlot : state.designatedPickupSlot,
      designatedDeliverySlot: data.deliverySlot !== undefined ? data.deliverySlot : state.designatedDeliverySlot,
      flexiblePickupMorning: data.flexPickupMorning !== undefined ? data.flexPickupMorning : state.flexiblePickupMorning,
      flexiblePickupAfternoon: data.flexPickupAfternoon !== undefined ? data.flexPickupAfternoon : state.flexiblePickupAfternoon,
      flexibleDeliveryMorning: data.flexDeliveryMorning !== undefined ? data.flexDeliveryMorning : state.flexibleDeliveryMorning,
      flexibleDeliveryAfternoon: data.flexDeliveryAfternoon !== undefined ? data.flexDeliveryAfternoon : state.flexibleDeliveryAfternoon,
      flexibleDeliveryAnytime: data.flexDeliveryAnytime !== undefined ? data.flexDeliveryAnytime : state.flexibleDeliveryAnytime,
      showDeliveryTimePanel: data.showDeliveryPanel !== undefined ? data.showDeliveryPanel : state.showDeliveryTimePanel,
      pickupDiscountRate: data.pickupDiscount !== undefined ? data.pickupDiscount : state.pickupDiscountRate,
      deliveryDiscountRate: data.deliveryDiscount !== undefined ? data.deliveryDiscount : state.deliveryDiscountRate,
    })),

  setPhone: (phone) => set({ phone }),

  setOriginPhone: (phone) => set({ originPhone: phone }),

  setAdditionalNote: (note) => set({ additionalNote: note }),

  setSlotSelectedAt: (ts) => set({ slotSelectedAt: ts }),

  setSlotConflictNotice: (msg) => set({ slotConflictNotice: msg }),

  setCarryHelp: (side, value) =>
    set(side === 'origin' ? { carryHelpOrigin: value } : { carryHelpDest: value }),

  setQuoteCarryHelp: (mode) => set({ quoteCarryHelp: mode }),

  setTermsAgreed: (key, value) =>
    set((state) => ({
      termsAgreed: { ...state.termsAgreed, [key]: value },
    })),

  setAllTermsAgreed: (value) =>
    set({
      termsAgreed: { terms: value, privacy: value, marketing: value },
    }),

  addItem: () =>
    set((state) => {
      if (!state.itemInfo.itemId) return state;
      const newItem: OrderItem = {
        ...state.itemInfo,
        uid: `item_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
        displayName: state.itemInfo.category || '물품',
      };
      return {
        items: [...state.items, newItem],
        itemInfo: { ...initialItemInfo, customData: state.itemInfo.customData },
      };
    }),

  removeItem: (uid) =>
    set((state) => ({
      items: state.items.filter((item) => item.uid !== uid),
    })),

  setCoupon: (code, discount) => set({ appliedCouponCode: code, couponDiscount: discount }),

  setQuoteId: (quoteId) => set({ quoteId }),

  setLockedFinalPrice: (v) => set({ lockedFinalPrice: v }),

  setQuoteExtraItem: (item) => set({ quoteExtraItem: item }),

  setCurrentStep: (step, total) => set((state) => ({
    currentStep: step,
    ...(total !== undefined ? { totalSteps: total } : {}),
  })),

  reset: () =>
    set({
      itemInfo: initialItemInfo,
      items: [],
      origin: null,
      destination: null,
      priceBreakdown: null,
      distanceKm: null,
      scheduleDate: null,
      designatedPickupSlot: null,
      designatedDeliverySlot: null,
      flexiblePickupMorning: false,
      flexiblePickupAfternoon: false,
      flexibleDeliveryMorning: false,
      flexibleDeliveryAfternoon: false,
      flexibleDeliveryAnytime: false,
      showDeliveryTimePanel: false,
      pickupDiscountRate: 0,
      deliveryDiscountRate: 0,
      slotSelectedAt: null,
      slotConflictNotice: null,
      phone: '',
      additionalNote: '',
      carryHelpOrigin: false,
      carryHelpDest: false,
      termsAgreed: initialTermsAgreed,
      appliedCouponCode: null,
      couponDiscount: 0,
      quoteId: null,
      lockedFinalPrice: null,
      quoteExtraItem: null,
      quoteCarryHelp: null,
      currentStep: 1,
      totalSteps: 5,
    }),
}));
